import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import axios from "./axiosConfig";
import PanelDisplay from "./PanelDisplay";
// @ts-ignore
import { v4 as uuidv4 } from "uuid";

type TenantMetricValueQueryItem = {
  name: string;
  value_query?: string | null;
  found_in_prometheus: boolean;
  remark?: string;
};

type TenantMetricValueQueryResult = {
  tenant_id: number;
  count: number;
  items: TenantMetricValueQueryItem[];
};

type TenantMetricCurrentValueResponse = {
  tenant_id: number;
  name: string;
  value_query: string;

  result_type?: string;
  result?: any;

  raw_value?: string | null;
  mapped_value?: string | null;
  display_value?: string | null;
  mapping_applied?: boolean;
};

type SelectedPanelItem = {
  order: number;
  type: string;
  data?: any;
  width?: number;
  height?: number;
  position_x?: number;
  position_y?: number;
  i?: string;
};

const DEFAULT_TRACEROUTE_API_ENDPOINT =
  "http://127.0.0.1:18090/traceroute?target=google.com&proto=udp";

function safeTrim(v: any) {
  return String(v ?? "").trim();
}

function isAbsoluteUrl(s: string) {
  return /^https?:\/\//i.test(s);
}

function parseQueryFromEndpoint(raw: string): { target: string; proto: string; port: string } {
  const v = safeTrim(raw);
  if (!v) return { target: "", proto: "", port: "" };

  try {
    const u = isAbsoluteUrl(v) ? new URL(v) : new URL(v, window.location.origin);
    const target = safeTrim(u.searchParams.get("target") || "");
    const proto = safeTrim(u.searchParams.get("proto") || "");
    const port = safeTrim(u.searchParams.get("port") || "");
    return { target, proto, port };
  } catch {
    const pick = (key: string) => {
      const m = v.match(new RegExp(`[?&]${key}=([^&]+)`, "i"));
      if (!m) return "";
      try {
        return safeTrim(decodeURIComponent(m[1]));
      } catch {
        return safeTrim(m[1]);
      }
    };
    return { target: pick("target"), proto: pick("proto"), port: pick("port") };
  }
}

const DashboardNewPanel: React.FC = () => {
  const [metricList, setMetricList] = useState<TenantMetricValueQueryItem[]>([]);
  const [selectedName, setSelectedName] = useState<string>("");
  const [currentDetail, setCurrentDetail] = useState<TenantMetricCurrentValueResponse | null>(null);

  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [loadingValue, setLoadingValue] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedPanels, setSelectedPanels] = useState<SelectedPanelItem[]>([]);

  const [isQuickQueryCollapsed, setIsQuickQueryCollapsed] = useState<boolean>(false);
  const [selectedChartType, setSelectedChartType] = useState<string>("");

  // Traceroute panel config (API URL only)
  const [tracerouteApiEndpoint, setTracerouteApiEndpoint] = useState<string>(DEFAULT_TRACEROUTE_API_ENDPOINT);
  const [tracerouteAutoRun, setTracerouteAutoRun] = useState<boolean>(true);
  const [tracerouteAutoRefreshSeconds, setTracerouteAutoRefreshSeconds] = useState<number>(60);

  const lastAutoQueriedNameRef = useRef<string>("");

  const tracerouteParsed = useMemo(
    () => parseQueryFromEndpoint(tracerouteApiEndpoint),
    [tracerouteApiEndpoint]
  );

  const fetchMetricList = async () => {
    setLoadingList(true);
    setError(null);
    try {
      const resp = await axios.get<TenantMetricValueQueryResult>("/api/tenant/metric-value-queries");
      const items = (resp.data?.items || []).filter((i) => i.found_in_prometheus);

      setMetricList(items);

      if (!selectedName && items.length > 0) {
        setSelectedName(items[0].name);
      }

      if (selectedName && !items.find((i) => i.name === selectedName) && items.length > 0) {
        setSelectedName(items[0].name);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load metric list.");
      setMetricList([]);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchCurrentValue = async (name: string) => {
    const safeName = safeTrim(name);
    if (!safeName) return;

    setLoadingValue(true);
    setError(null);
    try {
      const resp = await axios.get<TenantMetricCurrentValueResponse>("/api/tenant/metric-current-value", {
        params: { name: safeName },
      });
      setCurrentDetail(resp.data);
    } catch (e: any) {
      setCurrentDetail(null);
      setError(e?.response?.data?.message || e?.message || "Failed to query current value.");
    } finally {
      setLoadingValue(false);
    }
  };

  useEffect(() => {
    fetchMetricList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Do not auto query Prometheus in Traceroute mode
    if (selectedChartType === "TraceroutePanel") {
      setCurrentDetail(null);
      return;
    }

    if (!selectedName) {
      setCurrentDetail(null);
      return;
    }

    if (lastAutoQueriedNameRef.current === selectedName) return;

    lastAutoQueriedNameRef.current = selectedName;
    fetchCurrentValue(selectedName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedName, selectedChartType]);

  const selectedItem = useMemo(
    () => metricList.find((m) => m.name === selectedName),
    [metricList, selectedName]
  );

  const displayedValue =
    safeTrim(currentDetail?.display_value) ||
    safeTrim(currentDetail?.mapped_value) ||
    safeTrim(currentDetail?.raw_value) ||
    "";

  const valueQuery = safeTrim(currentDetail?.value_query ?? selectedItem?.value_query ?? "");

  const isNumericValue = (v: string) => {
    const s = safeTrim(v);
    if (!s) return false;
    const n = Number(s);
    return !Number.isNaN(n) && Number.isFinite(n);
  };

  const buildSolidColorCardData = (title: string, text: string) => {
    const normalized = safeTrim(text).toLowerCase();

    const safeKeywords = ["up", "established", "idle", "ok", "healthy", "running"];
    const unsafeKeywords = ["down", "critical", "failed", "error"];

    const isSafe =
      safeKeywords.some((k) => normalized === k || normalized.includes(k)) &&
      !unsafeKeywords.some((k) => normalized.includes(k));

    const safeColors = {
      textColor: "#FFFFFF",
      subTextColor: "#DFF5E1",
      backgroundColor: "#1F8B4C",
    };

    const unsafeColors = {
      textColor: "#FFFFFF",
      subTextColor: "#F6D6D6",
      backgroundColor: "#B02A37",
    };

    const colors = isSafe ? safeColors : unsafeColors;

    return {
      title,
      text,
      subText: "",
      textColor: colors.textColor,
      subTextColor: colors.subTextColor,
      backgroundColor: colors.backgroundColor,
    };
  };

  const buildGaugeChartData = (title: string, v: string) => {
    const num = Number(v);
    return {
      title,
      value: Number.isNaN(num) ? 0 : num,
      unit: "",
      isHealthIncreasing: true,
    };
  };

  const buildTimeSeriesChartData = (title: string, v: string) => {
    const num = Number(v);
    const safeNum = Number.isNaN(num) ? 0 : num;
    const nowIso = new Date().toISOString();

    return {
      title,
      data: [[nowIso, safeNum, ""]],
      unit: "",
    };
  };

  const buildTraceroutePanelData = () => {
    const ep = safeTrim(tracerouteApiEndpoint);

    const q = parseQueryFromEndpoint(ep);
    const t = q.target;

    const seconds = Number(tracerouteAutoRefreshSeconds);
    const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.max(5, Math.floor(seconds)) : 0;

    return {
      title: t ? `Traceroute to ${t}` : "Traceroute",
      apiEndpoint: ep,
      target: t, // parsed from apiEndpoint
      autoRun: !!tracerouteAutoRun,
      autoRefreshSeconds: safeSeconds,
      lastResult: null,
    };
  };

  const handleManualQuery = async () => {
    if (!selectedName) return;
    if (selectedChartType === "TraceroutePanel") return;

    lastAutoQueriedNameRef.current = "";
    await fetchCurrentValue(selectedName);
  };

  const handleAddPanel = () => {
    if (!selectedChartType) {
      setError("Please select a chart type first.");
      return;
    }

    if (selectedChartType === "TraceroutePanel") {
      const ep = safeTrim(tracerouteApiEndpoint);
      if (!ep) {
        setError("Please input Traceroute API URL first.");
        return;
      }

      const q = parseQueryFromEndpoint(ep);
      if (!q.target) {
        setError('Traceroute API URL must include "target=" (e.g. ?target=eu.obscpaas.com&proto=udp).');
        return;
      }

      const newOrder = selectedPanels.length;
      const panelId = uuidv4();

      const panelData = buildTraceroutePanelData();

      const newPanel: SelectedPanelItem = {
        i: panelId,
        order: newOrder,
        type: "TraceroutePanel",
        width: 12,
        height: 26,
        data: panelData,
        position_x: 0,
        position_y: 0,
      };

      setSelectedPanels((prev) => [...prev, newPanel]);
      return;
    }

    // Prometheus-driven panels
    if (!selectedName) {
      setError("Please select a Name first.");
      return;
    }

    if (!displayedValue) {
      setError("No current value yet. Please Query first.");
      return;
    }

    const newOrder = selectedPanels.length;
    const panelId = uuidv4();
    const title = selectedName;

    const panelBase = {
      metricName: selectedName,
      valueQuery,
    };

    let panelData: any;

    if (selectedChartType === "GaugeChart") {
      if (!isNumericValue(displayedValue)) {
        setError(
          `GaugeChart requires a numeric value, but got "${displayedValue}". Please choose another chart type or metric.`
        );
        return;
      }
      panelData = { ...panelBase, ...buildGaugeChartData(title, displayedValue) };
    } else if (selectedChartType === "SolidColorCard") {
      panelData = { ...panelBase, ...buildSolidColorCardData(title, displayedValue) };
    } else if (selectedChartType === "TimeSeriesChart") {
      if (!isNumericValue(displayedValue)) {
        setError(
          `TimeSeriesChart requires a numeric value, but got "${displayedValue}". Please choose another chart type or metric.`
        );
        return;
      }
      panelData = { ...panelBase, ...buildTimeSeriesChartData(title, displayedValue) };
    } else {
      setError(`Unsupported chart type "${selectedChartType}" for quick metric panel.`);
      return;
    }

    let width = 8;
    let height = 20;

    if (selectedChartType === "SolidColorCard") {
      width = 6;
      height = 18;
    } else if (selectedChartType === "TimeSeriesChart") {
      width = 12;
      height = 22;
    }

    const newPanel: SelectedPanelItem = {
      i: panelId,
      order: newOrder,
      type: selectedChartType,
      width,
      height,
      data: panelData,
      position_x: 0,
      position_y: 0,
    };

    setSelectedPanels((prev) => [...prev, newPanel]);
  };

  const handleRefreshAll = async () => {
    await fetchMetricList();
    if (selectedName && selectedChartType !== "TraceroutePanel") {
      lastAutoQueriedNameRef.current = "";
      await fetchCurrentValue(selectedName);
    }
  };

  const handleToggleQuickQuery = () => setIsQuickQueryCollapsed((prev) => !prev);

  const isTracerouteMode = selectedChartType === "TraceroutePanel";

  return (
    <Container fluid style={{ paddingTop: 10 }}>
      {error && (
        <Row>
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-2">
        <Col className="d-flex justify-content-end">
          <Button
            variant={isQuickQueryCollapsed ? "outline-light" : "outline-secondary"}
            size="sm"
            onClick={handleToggleQuickQuery}
          >
            {isQuickQueryCollapsed ? "Show Quick Query" : "Hide Quick Query"}
          </Button>
        </Col>
      </Row>

      <Row className="g-3 align-items-start">
        {!isQuickQueryCollapsed && (
          <Col xs={12} lg={4} xl={3}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                marginBottom: 8,
                gap: 10,
              }}
            >
              <div>
                <h5 style={{ marginBottom: 4 }}>Tenant Metric Quick Query</h5>
                <div style={{ opacity: 0.7, fontSize: 11 }}>
                  Pick a metric name / Traceroute API URL, choose a chart type, then add panel.
                </div>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={handleRefreshAll}>
                  Refresh
                </Button>
              </div>
            </div>

            <Card
              style={{
                borderRadius: 10,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Card.Body>
                <Form.Group controlId="chartTypeSelect">
                  <Form.Label style={{ fontWeight: 600, marginBottom: 6 }}>Chart Type</Form.Label>
                  <Form.Select
                    value={selectedChartType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedChartType(v);

                      if (v === "TraceroutePanel") {
                        setCurrentDetail(null);
                        setError(null);
                      }
                    }}
                  >
                    <option value="">Select chart type</option>
                    <option value="SolidColorCard">Status Card (Solid Color)</option>
                    <option value="GaugeChart">Gauge Chart (numeric)</option>
                    <option value="TimeSeriesChart">Time Series Chart</option>
                    <option value="TraceroutePanel">Traceroute Panel</option>
                  </Form.Select>
                </Form.Group>

                {!isTracerouteMode && (
                  <Form.Group controlId="metricNameSelect" className="mt-3">
                    <Form.Label style={{ fontWeight: 600, marginBottom: 6 }}>Name</Form.Label>
                    <Form.Select
                      value={selectedName}
                      disabled={loadingList || metricList.length === 0}
                      onChange={(e) => setSelectedName(e.target.value)}
                    >
                      {loadingList && <option value="">Loading...</option>}
                      {!loadingList && metricList.length === 0 && (
                        <option value="">No rules found in Prometheus</option>
                      )}
                      {!loadingList &&
                        metricList.map((m) => (
                          <option key={m.name} value={m.name}>
                            {m.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                )}

                {isTracerouteMode && (
                  <>
                    <Form.Group controlId="tracerouteApiEndpointInput" className="mt-3">
                      <Form.Label style={{ fontWeight: 600, marginBottom: 6 }}>API URL</Form.Label>
                      <Form.Control
                        type="text"
                        value={tracerouteApiEndpoint}
                        onChange={(e) => setTracerouteApiEndpoint(e.target.value)}
                        placeholder="e.g. http://172.17.40.156:18090/traceroute?target=eu.obscpaas.com&proto=udp"
                      />
                      <div style={{ marginTop: 6, fontSize: 10, opacity: 0.65 }}>
                        Called via backend <code>/api/api-sources/test</code> (same as ApiSourceForm Test). Must include{" "}
                        <code>target=</code>.
                      </div>
                    </Form.Group>

                    <Row className="mt-3 g-2">
                      <Col xs={12}>
                        <Form.Group controlId="tracerouteTargetParsed">
                          <Form.Label style={{ fontWeight: 600, marginBottom: 6 }}>Target (parsed)</Form.Label>
                          <Form.Control
                            type="text"
                            value={tracerouteParsed.target}
                            readOnly
                            placeholder='e.g. ...?target=eu.obscpaas.com&proto=udp'
                          />
                          <div style={{ marginTop: 6, fontSize: 10, opacity: 0.65 }}>
                            proto: <code>{tracerouteParsed.proto || "(default)"}</code>{" "}
                            <span className="ms-2">
                              port: <code>{tracerouteParsed.port || "(default)"}</code>
                            </span>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mt-3 g-2">
                      <Col xs={6}>
                        <Form.Group controlId="tracerouteAutoRun">
                          <Form.Label style={{ fontWeight: 600, marginBottom: 6 }}>Auto Run</Form.Label>
                          <Form.Check
                            type="switch"
                            checked={tracerouteAutoRun}
                            onChange={(e) => setTracerouteAutoRun(e.target.checked)}
                            label={tracerouteAutoRun ? "Enabled" : "Disabled"}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={6}>
                        <Form.Group controlId="tracerouteAutoRefreshSeconds">
                          <Form.Label style={{ fontWeight: 600, marginBottom: 6 }}>Refresh (sec)</Form.Label>
                          <Form.Control
                            type="number"
                            min={0}
                            step={1}
                            value={tracerouteAutoRefreshSeconds}
                            onChange={(e) => setTracerouteAutoRefreshSeconds(Number(e.target.value))}
                            placeholder="e.g. 60 (0=off)"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div style={{ marginTop: 8, fontSize: 10, opacity: 0.6 }}>
                      Auto refresh uses TraceroutePanel internal timer. 0 means disabled.
                    </div>
                  </>
                )}

                <div className="d-flex gap-2 mt-3">
                  {!isTracerouteMode && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleManualQuery}
                      disabled={!selectedName || loadingValue}
                    >
                      {loadingValue ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-1" />
                          Query
                        </>
                      ) : (
                        "Query"
                      )}
                    </Button>
                  )}

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddPanel}
                    disabled={
                      !selectedChartType ||
                      (isTracerouteMode ? false : !selectedName || !displayedValue)
                    }
                  >
                    Add Panel
                  </Button>
                </div>

                {!isTracerouteMode && (
                  <>
                    <hr style={{ opacity: 0.15 }} />

                    <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>Value Query (PromQL)</div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        lineHeight: 1.4,
                        background: "rgba(0,0,0,0.15)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 8,
                        padding: "8px 10px",
                        minHeight: 52,
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                      }}
                    >
                      {valueQuery || <span style={{ opacity: 0.5 }}>No value query available.</span>}
                    </div>

                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 12, marginBottom: 6 }}>Current Value</div>
                    <div
                      style={{
                        background: "rgba(0,0,0,0.12)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 8,
                        padding: "10px 12px",
                        minHeight: 44,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {displayedValue || (
                          <span style={{ opacity: 0.5, fontWeight: 400 }}>No data</span>
                        )}
                      </div>

                      {currentDetail?.mapping_applied ? (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: 10,
                            background: "rgba(25,135,84,0.2)",
                            border: "1px solid rgba(25,135,84,0.5)",
                            color: "#9be3b7",
                            whiteSpace: "nowrap",
                          }}
                        >
                          mapped
                        </span>
                      ) : null}
                    </div>

                    <div style={{ marginTop: 8, fontSize: 10, opacity: 0.5 }}>
                      Auto query triggers on selection. Display order: display_value → mapped_value → raw_value.
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        <Col xs={12} lg={isQuickQueryCollapsed ? 12 : 8} xl={isQuickQueryCollapsed ? 12 : 9}>
          <PanelDisplay
            selectedPanels={selectedPanels as any}
            onRemovePanel={(arg: any) => {
              // safe remove: treat arg as index for new panels
              setSelectedPanels((prev) => {
                const idx = Number(arg);
                if (!Number.isFinite(idx)) return prev;
                if (idx >= 0 && idx < prev.length) return prev.filter((_, i) => i !== idx);
                return prev;
              });
            }}
            onUpdatePanels={(newPanels) => setSelectedPanels(newPanels as any)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default DashboardNewPanel;
